/** Whether what was typed into Delete job confirms the job's name.
 *
 * The name's letters and digits must be typed in order: typing it out is what proves the
 * person means this job, and the number of a copy ("Accounts Payable (9)") still counts.
 * Capitals, spaces and punctuation are not compared (owner, 2026-10-19): the app's own job
 * names carry " · " (every sample job is "Sample · Accounts Payable" and so on), a
 * character a US keyboard cannot type, so "Sample Accounts Payable" and "sample accounts
 * payable" both confirm it. A name with no letters or digits at all ("***") is still typed
 * exactly. The job deleted is the one the dialog was opened for, never one looked up by the
 * typed text, so a looser match cannot reach another job. */
export function confirmsJobName(typed: string, name: string): boolean {
  if (typed === name) return true;
  const key = nameKey(name);
  return key.length > 0 && nameKey(typed) === key;
}

/** The job's name as Delete job asks for it: what a keyboard can type. The " · " in the
 * app's own job names (and a bullet) reads as a space, so the line says "Type Sample
 * Accounts Payable (9) to confirm" and nobody hunts for the dot. Everything else stays. */
export function typeableJobName(name: string): string {
  return (
    name
      .replace(/\s*[·•]\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || name
  );
}

/** Letters, digits and their marks, in order, any case; compatibility forms folded (ﬁ, full-width digits). */
const nameKey = (text: string) =>
  text
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N}\p{M}]+/gu, '')
    .toLowerCase();
